import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import { cachedRead } from "services/usageCache";

/**
 * Daily usage rollups (`llm_usage_daily`), written by functions/src/nlp/query_llm.js.
 *
 * WHY THIS EXISTS: the Usage Monitor used to sum spend by reading every firestore_llmcalls
 * doc in the selected range — one billed read per LLM call, growing without bound, and each
 * of those docs carries the composed prompt and the model's output, so rendering a cost
 * total shipped patient records into a browser. Reading one counter doc per day makes the
 * same page ~30 reads at any volume, and the counters hold no PHI.
 *
 * Doc ids are YYYY-MM-DD, so a documentId() range query is an ordered scan of exactly the
 * days requested.
 */

const COLLECTION = "llm_usage_daily";

/** UTC, to match the keys the backend writes. Local dates would drop or double a day. */
export const toDateKey = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Every date key in the window, oldest first, including days with no activity. */
export const dateKeysForRange = (days) => {
  const keys = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - i);
    keys.push(toDateKey(d));
  }
  return keys;
};

/** The dimensions a breakdown can group by — mirrors the rollup's map fields. */
export const GROUP_BY_FIELDS = ["byFeature", "byModel", "byProvider", "byOrg", "byUser"];

const emptyDay = (dateKey) => ({
  dateKey,
  calls: 0,
  failedCalls: 0,
  unpricedCalls: 0,
  simulatedCalls: 0,
  simulatedCost: 0,
  cost: 0,
  promptTokens: 0,
  completionTokens: 0,
  encountersCompleted: 0,
  byProvider: {},
  byModel: {},
  byOrg: {},
  byFeature: {},
  byUser: {},
  bySession: {},
});

const mergeBreakdown = (target, source) => {
  Object.entries(source || {}).forEach(([key, value]) => {
    if (!target[key]) {
      target[key] = { cost: 0, calls: 0, inputTokens: 0, outputTokens: 0 };
    }
    target[key].cost += Number(value?.cost ?? 0);
    target[key].calls += Number(value?.calls ?? 0);
    target[key].inputTokens += Number(value?.inputTokens ?? 0);
    target[key].outputTokens += Number(value?.outputTokens ?? 0);
  });
};

/**
 * Fetch the rollups for the last `days` days.
 *
 * Returns one entry per day (zero-filled, so charts don't need to reconcile gaps) plus
 * summed totals and breakdowns across the window.
 *
 * Cached per window so navigating away and back does not re-read up to 90 documents. The
 * cache key includes today's date so the window silently rolls over at UTC midnight instead
 * of serving yesterday's range.
 */
export function fetchUsageRollups(days) {
  return cachedRead(`rollups:${days}:${toDateKey(new Date())}`, () => readUsageRollups(days));
}

async function readUsageRollups(days) {
  const keys = dateKeysForRange(days);
  const startKey = keys[0];
  const endKey = keys[keys.length - 1];

  const snap = await db
    .collection(COLLECTION)
    .where(firebase.firestore.FieldPath.documentId(), ">=", startKey)
    .where(firebase.firestore.FieldPath.documentId(), "<=", endKey)
    .get();

  const byKey = {};
  snap.forEach((doc) => {
    const d = doc.data() || {};
    byKey[doc.id] = {
      ...emptyDay(doc.id),
      ...d,
      cost: Number(d.cost ?? 0),
      calls: Number(d.calls ?? 0),
      failedCalls: Number(d.failedCalls ?? 0),
      unpricedCalls: Number(d.unpricedCalls ?? 0),
      promptTokens: Number(d.promptTokens ?? 0),
      completionTokens: Number(d.completionTokens ?? 0),
      encountersCompleted: Number(d.encountersCompleted ?? 0),
      simulatedCalls: Number(d.simulatedCalls ?? 0),
      simulatedCost: Number(d.simulatedCost ?? 0),
      byProvider: d.byProvider || {},
      byModel: d.byModel || {},
      byOrg: d.byOrg || {},
      byFeature: d.byFeature || {},
      byUser: d.byUser || {},
      bySession: d.bySession || {},
    };
  });

  const dailyRows = keys.map((key) => byKey[key] || emptyDay(key));

  const totals = dailyRows.reduce(
    (acc, day) => {
      acc.cost += day.cost;
      acc.calls += day.calls;
      acc.failedCalls += day.failedCalls;
      acc.unpricedCalls += day.unpricedCalls;
      acc.simulatedCalls += day.simulatedCalls;
      acc.simulatedCost += day.simulatedCost;
      acc.promptTokens += day.promptTokens;
      acc.completionTokens += day.completionTokens;
      acc.encountersCompleted += day.encountersCompleted;
      GROUP_BY_FIELDS.forEach((field) => mergeBreakdown(acc[field], day[field]));
      // Encounters are keyed by id, so the same encounter spanning midnight merges
      // rather than counting twice.
      Object.entries(day.bySession || {}).forEach(([id, value]) => {
        if (!acc.bySession[id]) {
          acc.bySession[id] = {
            cost: 0,
            calls: 0,
            inputTokens: 0,
            outputTokens: 0,
            feature: value?.feature || "unknown",
            isSimulation: Boolean(value?.isSimulation),
          };
        }
        acc.bySession[id].cost += Number(value?.cost ?? 0);
        acc.bySession[id].calls += Number(value?.calls ?? 0);
        acc.bySession[id].inputTokens += Number(value?.inputTokens ?? 0);
        acc.bySession[id].outputTokens += Number(value?.outputTokens ?? 0);
      });
      return acc;
    },
    {
      cost: 0,
      calls: 0,
      failedCalls: 0,
      unpricedCalls: 0,
      simulatedCalls: 0,
      simulatedCost: 0,
      promptTokens: 0,
      completionTokens: 0,
      encountersCompleted: 0,
      byProvider: {},
      byModel: {},
      byOrg: {},
      byFeature: {},
      byUser: {},
      bySession: {},
    },
  );

  return { dailyRows, totals, docsRead: snap.size };
}

/**
 * Cost per encounter as a distribution, not an average.
 *
 * An average hides the thing worth knowing: whether a long consultation costs ten times a
 * short one. p90 is what you quote when someone asks about a busy clinic day, and the median
 * is what a typical visit actually costs.
 *
 * `feature` filters to one product (encounters are per-feature); simulation encounters are
 * excluded by default because admin prompt-development is real spend but not clinical usage.
 */
export function encounterCostStats(totals, { feature = "chartmind", includeSimulation = false } = {}) {
  const costs = Object.values(totals?.bySession || {})
    .filter((s) => (feature ? s.feature === feature : true))
    .filter((s) => (includeSimulation ? true : !s.isSimulation))
    .map((s) => s.cost)
    .sort((a, b) => a - b);

  if (!costs.length) return null;

  const at = (fraction) => costs[Math.min(costs.length - 1, Math.floor(costs.length * fraction))];
  const sum = costs.reduce((a, b) => a + b, 0);

  return {
    encounters: costs.length,
    total: sum,
    mean: sum / costs.length,
    median: at(0.5),
    p90: at(0.9),
    max: costs[costs.length - 1],
  };
}

/**
 * Active users — those who actually generated an AI call in the window.
 *
 * Registered-but-never-used accounts are deliberately not the denominator: including them
 * makes cost per user shrink as signups grow, which reads as efficiency when nothing about
 * the product changed. Every AI usage dashboard worth copying counts active users.
 */
export function activeUserCount(totals) {
  return Object.keys(totals?.byUser || {}).length;
}

/**
 * Straight-line projection to month end from this month's spend so far.
 *
 * Deliberately naive — days elapsed vs. days in month. It answers "roughly what will this
 * month cost", and dressing it up as a forecast would imply more than the input supports.
 */
export function projectMonthlySpend(dailyRows) {
  const now = new Date();
  const thisMonth = toDateKey(now).slice(0, 7);
  const monthRows = dailyRows.filter((row) => row.dateKey.startsWith(thisMonth));
  if (!monthRows.length) return null;

  const spentSoFar = monthRows.reduce((sum, row) => sum + row.cost, 0);
  const dayOfMonth = now.getUTCDate();
  const daysInMonth = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0),
  ).getUTCDate();

  // Only meaningful once the window actually covers the month so far.
  if (monthRows.length < dayOfMonth) return null;

  return {
    spentSoFar,
    projected: (spentSoFar / dayOfMonth) * daysInMonth,
    dayOfMonth,
    daysInMonth,
  };
}
